import React from 'react';
import styles from './toggle-input.module.css';

/**
 * ToggleInput component renders a pill-shaped toggle switch (styled checkbox).
 * Used to let users opt in or out of optional personalisation slots such as UV inserts.
 *
 * @param {Object} props - Component props.
 * @param {string} props.id - Unique id for the input element.
 * @param {boolean} props.checked - Whether the toggle is currently on.
 * @param {function} props.onChange - Callback receiving the new boolean value when toggled.
 * @param {import('react').ReactNode} [props.label] - Label content displayed alongside the toggle. Accepts text or elements (e.g. an inline price span).
 * @param {string} [props.ariaLabel] - Plain-text accessible label for the input, announced by screen readers. Required when label contains JSX. Falls back to label when label is a plain string.
 * @param {string} [props.description] - Description text displayed below the label.
 * @param {boolean} [props.disabled] - Whether the toggle is disabled.
 * @returns {import('react').JSX.Element}
 */
export const ToggleInput = ({ id, checked = false, onChange, label, ariaLabel, description, disabled = false }) => {
  /**
   * Forwards the native checkbox event as a plain boolean to the consumer.
   *
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - The native change event.
   */
  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.checked);
    }
  };

  return (
    <div className={styles['toggle-input']}>
      <div className={styles['toggle-input__text']}>
        {label && <span className={styles['toggle-input__label-text'] + ' body-base font-medium'}>{label}</span>}
        {description && (
          <div className="rte body-sm" id={`${id}-description`} dangerouslySetInnerHTML={{ __html: description }} />
        )}
      </div>
      <label className={styles['toggle-input__label']} htmlFor={id}>
        <span className={styles['toggle-input__track']} aria-hidden="true" />
        <input
          className={styles['toggle-input__input']}
          id={id}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          aria-checked={checked}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          aria-describedby={`${id}-description`}
        />
      </label>
    </div>
  );
};
